import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, ArrowUpRight, Calendar, Clock, TrendingUp, ExternalLink } from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { SITE, breadcrumbForPage } from "@/lib/seo";
import { getArticleBySlug, ARTICLES } from "@/data/articles-loader";
import { getAuthorForArticle, authorPersonSchema } from "@/data/authors";

const categoryColors: Record<string, string> = {
  IA: "bg-primary/10 text-primary border-primary/20",
  Negocios: "bg-amber-500/10 text-amber-700 border-amber-500/20",
  Economía: "bg-emerald-500/10 text-emerald-700 border-emerald-500/20",
  Tecnología: "bg-blue-500/10 text-blue-700 border-blue-500/20",
  Marketing: "bg-pink-500/10 text-pink-700 border-pink-500/20",
};

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" });

const ArticuloPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
  }, [slug]);

  if (!article) {
    return <Navigate to="/noticias" replace />;
  }

  const url = `${SITE.url}/noticias/${article.slug}`;
  const seoTitle = article.seoTitle ?? `${article.title} | Vibra Marketing`;
  const seoDescription = article.seoDescription ?? article.summary;
  const author = getAuthorForArticle(article.category);

  const related = ARTICLES.filter(
    (a) => a.slug !== article.slug && a.category === article.category,
  ).slice(0, 3);

  const personSchema = authorPersonSchema(author);

  const newsArticleSchema = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    headline: article.title,
    description: seoDescription,
    datePublished: article.date,
    dateModified: article.date,
    inLanguage: "es-ES",
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    url,
    image: SITE.ogImage,
    keywords: article.keywords?.join(", "),
    articleSection: article.category,
    author: {
      "@type": "Person",
      name: author.name,
      jobTitle: author.role,
      url: `${SITE.url}/#${author.id}`,
      sameAs: author.sameAs ?? [],
    },
    publisher: {
      "@type": "Organization",
      name: SITE.name,
      logo: { "@type": "ImageObject", url: `${SITE.url}/favicon.png` },
    },
    isBasedOn: article.sourceUrl,
  };

  const faqSchema = article.faqs?.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: article.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }
    : null;

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        {/* Estilos .prose extraídos del CSS principal — solo se cargan en páginas de artículo */}
        <link rel="stylesheet" href="/prose.css" />
        <html lang="es" />
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        {article.keywords?.length ? (
          <meta name="keywords" content={article.keywords.join(", ")} />
        ) : null}
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
        <link rel="canonical" href={url} />
        <link rel="alternate" hrefLang="es-ES" href={url} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Vibra Marketing" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={url} />
        <meta property="og:image" content={SITE.ogImage} />
        <meta property="og:locale" content="es_ES" />
        <meta property="article:published_time" content={article.date} />
        <meta property="article:section" content={article.category} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <script type="application/ld+json">{JSON.stringify(newsArticleSchema)}</script>
        <script type="application/ld+json">
          {JSON.stringify(
            breadcrumbForPage([
              { name: "Inicio", path: "/" },
              { name: "Noticias", path: "/noticias" },
              { name: article.title, path: `/noticias/${article.slug}` },
            ]),
          )}
        </script>
        <script type="application/ld+json">{JSON.stringify(personSchema)}</script>
        {faqSchema ? (
          <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        ) : null}
      </Helmet>
      <Header />

      <main className="relative pt-32 pb-20">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent pointer-events-none" />

        <div className="container mx-auto px-6 relative">
          {/* Back link */}
          <Link
            to="/noticias"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" /> Volver a Noticias
          </Link>

          {/* Header */}
          <header className="max-w-3xl mb-10">
            <div className="flex items-center gap-2 mb-5 flex-wrap">
              <span
                className={cn(
                  "px-2.5 py-1 rounded-full border text-[11px] font-medium",
                  categoryColors[article.category],
                )}
              >
                {article.category}
              </span>
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <Calendar className="w-3 h-3" />
                {formatDate(article.date)}
              </span>
              {article.readingTime ? (
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Clock className="w-3 h-3" />
                  {article.readingTime} min de lectura
                </span>
              ) : null}
              <span className="text-xs text-muted-foreground">· Fuente: {article.source}</span>
            </div>

            <h1 className="font-display text-3xl md:text-5xl uppercase leading-[1.05] mb-5">
              {article.title}
            </h1>
            <p className="text-muted-foreground text-lg md:text-xl leading-relaxed">
              {article.summary}
            </p>

            {/* Author byline */}
            <div className="mt-6 flex items-center gap-3">
              <img
                src={author.image}
                alt={`${author.name} — ${author.role}`}
                className="w-11 h-11 rounded-full object-cover border border-border"
                loading="lazy"
              />
              <div className="text-sm leading-tight">
                <div className="font-medium">
                  Por{" "}
                  {author.linkedin ? (
                    <a
                      href={author.linkedin}
                      target="_blank"
                      rel="noopener noreferrer author"
                      className="hover:text-primary transition-colors"
                    >
                      {author.name}
                    </a>
                  ) : (
                    author.name
                  )}
                </div>
                <div className="text-xs text-muted-foreground">{author.role}</div>
              </div>
            </div>
          </header>

          <div className="grid grid-cols-1 lg:grid-cols-[1fr,320px] gap-10 max-w-6xl">
            {/* Main body */}
            <div className="min-w-0">
              {/* TL;DR */}
              {article.tldr && (
                <div className="rounded-2xl border border-primary/20 bg-primary/5 p-6 md:p-7 mb-10">
                  <div className="text-primary text-xs font-semibold uppercase tracking-wider mb-2">
                    TL;DR
                  </div>
                  <p className="text-sm md:text-base leading-relaxed">{article.tldr}</p>
                </div>
              )}

              {/* Impact box */}
              <div className="rounded-2xl bg-gradient-to-br from-primary/8 via-card to-purple-500/5 border border-primary/15 p-6 md:p-7 mb-10">
                <div className="flex items-center gap-2 text-primary text-xs font-semibold uppercase tracking-wider mb-2">
                  <TrendingUp className="w-3.5 h-3.5" />
                  Impacto en tu negocio online
                </div>
                <p className="text-sm md:text-base leading-relaxed">{article.impact}</p>
              </div>

              {/* Body markdown */}
              <div className="article-prose prose prose-neutral max-w-none prose-headings:font-display prose-headings:uppercase prose-h2:text-2xl md:prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3 prose-p:leading-relaxed prose-p:text-base prose-li:leading-relaxed prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-strong:text-foreground">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{article.body}</ReactMarkdown>
              </div>

              {/* FAQs */}
              {article.faqs?.length ? (
                <section className="mt-14">
                  <h2 className="font-display text-2xl md:text-3xl uppercase mb-6">
                    Preguntas frecuentes
                  </h2>
                  <div className="space-y-4">
                    {article.faqs.map((f, i) => (
                      <details
                        key={i}
                        className="group rounded-2xl border border-border bg-card p-5 md:p-6"
                      >
                        <summary className="cursor-pointer list-none font-semibold text-base md:text-lg flex items-start justify-between gap-4">
                          <span>{f.q}</span>
                          <span className="text-primary group-open:rotate-45 transition-transform text-xl leading-none">
                            +
                          </span>
                        </summary>
                        <p className="mt-3 text-sm md:text-base text-muted-foreground leading-relaxed">
                          {f.a}
                        </p>
                      </details>
                    ))}
                  </div>
                </section>
              ) : null}

              {/* Sources */}
              {article.sources?.length ? (
                <section className="mt-14">
                  <h2 className="font-display text-xl uppercase mb-4">Fuentes</h2>
                  <ul className="space-y-2">
                    {article.sources.map((s, i) => (
                      <li key={i}>
                        <a
                          href={s.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                          {s.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </section>
              ) : null}

              {/* Author bio (E-E-A-T) */}
              <aside className="mt-14 rounded-2xl border border-border bg-card p-6 md:p-7 flex flex-col sm:flex-row gap-5 items-start">
                <img
                  src={author.image}
                  alt={`Foto de ${author.name}, ${author.role}`}
                  className="w-20 h-20 rounded-full object-cover border border-border shrink-0"
                  loading="lazy"
                />
                <div className="min-w-0">
                  <div className="text-xs font-semibold uppercase tracking-wider text-primary mb-1">
                    Sobre el autor
                  </div>
                  <div className="font-display text-lg uppercase">{author.name}</div>
                  <div className="text-sm text-muted-foreground mb-2">{author.role}</div>
                  <p className="text-sm leading-relaxed">{author.bio}</p>
                  {author.linkedin ? (
                    <a
                      href={author.linkedin}
                      target="_blank"
                      rel="noopener noreferrer author"
                      className="inline-flex items-center gap-1.5 mt-3 text-sm text-primary hover:underline"
                    >
                      LinkedIn <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  ) : null}
                </div>
              </aside>

              {/* CTA back */}
              <div className="mt-14 flex flex-wrap gap-3">
                <a
                  href={article.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full border border-border hover:border-primary/40 hover:text-primary text-sm font-medium transition-colors"
                >
                  Ver fuente original ({article.source}) <ArrowUpRight className="w-4 h-4" />
                </a>
                <Link
                  to="/noticias"
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-foreground text-background hover:opacity-90 text-sm font-medium transition-opacity"
                >
                  <ArrowLeft className="w-4 h-4" /> Más noticias
                </Link>
              </div>
            </div>

            {/* Sidebar */}
            <aside className="hidden lg:block">
              <div className="sticky top-28 space-y-6">
                <div className="rounded-2xl border border-border bg-card p-6">
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                    Sobre esta noticia
                  </div>
                  <dl className="space-y-2 text-sm">
                    <div>
                      <dt className="text-muted-foreground">Categoría</dt>
                      <dd className="font-medium">{article.category}</dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">Publicado</dt>
                      <dd className="font-medium">{formatDate(article.date)}</dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">Fuente</dt>
                      <dd>
                        <a
                          href={article.sourceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-primary hover:underline"
                        >
                          {article.source} <ExternalLink className="w-3 h-3" />
                        </a>
                      </dd>
                    </div>
                  </dl>
                </div>

                {related.length ? (
                  <div className="rounded-2xl border border-border bg-card p-6">
                    <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                      Relacionadas
                    </div>
                    <ul className="space-y-3">
                      {related.map((r) => (
                        <li key={r.slug}>
                          <Link
                            to={`/noticias/${r.slug}`}
                            className="group block text-sm font-medium leading-snug hover:text-primary transition-colors"
                          >
                            {r.title}
                            <span className="block text-[11px] text-muted-foreground mt-0.5 font-normal">
                              {formatDate(r.date)}
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            </aside>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ArticuloPage;
